// src/features/booking/ResourceFormDialog.tsx
// Create / edit a bookable resource (room, equipment, trainer, vehicle, ...).
// Desktop-app create dialog (docs/ux/14-forms.md): ghost title/description,
// core properties in the chip row, rare fields behind "+ more fields".

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import type { SelectOption } from '@/components/Select';
import { ChipInput } from './ChipInput';
import { ImageUrlList } from './ImageUrlList';
import { errMessage } from './lib';
import { CreateResource } from '@core/api/endpoints/booking/create_resource';
import { UpdateResource } from '@core/api/endpoints/booking/update_resource';
import type { BookableResource, BookingTypeDefinition, PricingModel } from '@core/types/booking';
import type { Entity } from '@core/types/entity';

const PRICING_MODELS: PricingModel[] = [
  'PRICING_MODEL_FIXED',
  'PRICING_MODEL_HOURLY',
  'PRICING_MODEL_DAILY',
  'PRICING_MODEL_PER_SESSION',
  'PRICING_MODEL_PER_PERSON',
  'PRICING_MODEL_CUSTOM',
];

function digits(v: string, allowDot = false): string {
  return allowDot ? v.replace(/[^0-9.]/g, '') : v.replace(/[^0-9]/g, '');
}

interface Props {
  open: boolean;
  onClose: () => void;
  resource: BookableResource | null;
  bookingTypes: BookingTypeDefinition[];
  scope: { productId: string; platformId: string; tenantId: string };
  onSaved: () => void;
}

export function ResourceFormDialog({ open, onClose, resource, bookingTypes, scope, onSaved }: Props) {
  const { t } = useI18n();
  const isEdit = !!resource;
  const descRef = useRef<HTMLTextAreaElement>(null);

  const [name, setName] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [bookingTypeId, setBookingTypeId] = useState('');
  const [capacity, setCapacity] = useState('1');
  const [pricingModel, setPricingModel] = useState<PricingModel>('PRICING_MODEL_HOURLY');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [locationName, setLocationName] = useState('');
  const [address, setAddress] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [moreFields, setMoreFields] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (resource) {
      setName(resource.name ?? '');
      setShortDescription(resource.short_description ?? '');
      setDescription(resource.description ?? '');
      setBookingTypeId(resource.booking_type_id ?? '');
      setCapacity(resource.capacity ? String(resource.capacity) : '1');
      setPricingModel(resource.pricing?.model ?? 'PRICING_MODEL_HOURLY');
      setPrice(resource.pricing?.base_price_cents !== undefined ? String(resource.pricing.base_price_cents / 100) : '');
      setCurrency(resource.pricing?.currency || 'USD');
      setLocationName(resource.location?.display_name ?? '');
      setAddress(resource.location?.address ?? '');
      setCoverImageUrl(resource.cover_image_url ?? '');
      setImageUrls(resource.image_urls ?? []);
      setCategories(resource.categories ?? []);
    } else {
      setName('');
      setShortDescription('');
      setDescription('');
      setBookingTypeId(bookingTypes[0]?.entity?.id ?? '');
      setCapacity('1');
      setPricingModel('PRICING_MODEL_HOURLY');
      setPrice('');
      setCurrency('USD');
      setLocationName('');
      setAddress('');
      setCoverImageUrl('');
      setImageUrls([]);
      setCategories([]);
    }
    setMoreFields(false);
    setError(null);
  }, [open, resource, bookingTypes]);

  const typeOptions: SelectOption[] = bookingTypes.map((bt) => ({
    value: bt.entity?.id ?? '',
    label: bt.display_name || bt.slug,
  }));

  const pricingOptions: SelectOption[] = PRICING_MODELS.map((m) => ({
    value: m,
    label: t(`booking.resource.pricingModel.${m.replace('PRICING_MODEL_', '').toLowerCase()}`),
  }));

  const dirty = !!(
    name.trim() ||
    description.trim() ||
    shortDescription.trim() ||
    imageUrls.length ||
    categories.length
  );

  const submit = async () => {
    if (!name.trim()) {
      setError(t('booking.resource.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    const priceCents = price === '' ? undefined : Math.round(parseFloat(price) * 100);
    try {
      if (isEdit && resource?.entity) {
        await UpdateResource(resource.entity.id, {
          resource: {
            entity: resource.entity,
            name,
            short_description: shortDescription,
            description,
            booking_type_id: bookingTypeId,
            capacity: parseInt(capacity || '1', 10),
            pricing: { model: pricingModel, base_price_cents: priceCents ?? 0, currency },
            location: { display_name: locationName, address },
            cover_image_url: coverImageUrl,
            image_urls: imageUrls,
            categories,
            tags: resource.tags ?? [],
          },
          update_mask: [
            'name', 'short_description', 'description', 'booking_type_id', 'capacity',
            'pricing', 'location', 'cover_image_url', 'image_urls', 'categories',
          ],
        });
      } else {
        const entity: Entity = { type_id: '', type_name: 'bookable_resource', id: '', platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId };
        await CreateResource({
          entity,
          resource: {
            name,
            short_description: shortDescription,
            description,
            booking_type_id: bookingTypeId,
            capacity: parseInt(capacity || '1', 10),
            pricing: { model: pricingModel, base_price_cents: priceCents ?? 0, currency },
            location: { display_name: locationName, address },
            cover_image_url: coverImageUrl,
            image_urls: imageUrls,
            categories,
            tags: [],
          },
        });
      }
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(errMessage(e, t('booking.resource.saveError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isEdit ? t('common.saveChanges') : t('booking.resource.createSubmit')}
      canSubmit={!!name.trim()}
      isSubmitting={saving}
      dirty={dirty}
      width="lg"
      aria-label={isEdit ? t('booking.resource.editTitle') : t('booking.resource.createTitle')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="px-3.5 pb-1 pt-2">
        {error && (
          <div className="pb-2">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={`${t('booking.resource.field.name')}…`}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          aria-label={t('booking.resource.field.name')}
        />
        <AutoGrowTextarea
          ref={descRef}
          value={description}
          onChange={setDescription}
          placeholder={`${t('booking.resource.field.description')}…`}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3 pt-1">
        <div className="w-48">
          <Select
            value={bookingTypeId}
            onChange={(e) => setBookingTypeId(e.target.value)}
            options={typeOptions}
            placeholder={t('booking.resource.field.bookingTypePlaceholder')}
            aria-label={t('booking.resource.field.bookingType')}
            fullWidth
          />
        </div>
        <div className="w-40">
          <Select
            value={pricingModel}
            onChange={(e) => setPricingModel(e.target.value as PricingModel)}
            options={pricingOptions}
            aria-label={t('booking.resource.field.pricingModel')}
            fullWidth
          />
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <span className="text-xs text-muted-foreground">{t('booking.resource.field.capacity')}</span>
          <input
            type="text"
            inputMode="numeric"
            value={capacity}
            onChange={(e) => setCapacity(digits(e.target.value))}
            aria-label={t('booking.resource.field.capacity')}
            className="w-10 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
        </span>
      </div>

      {moreFields && (
        <div className="space-y-1 border-t border-border px-3.5 py-2">
          <FormRow label={t('booking.resource.field.shortDescription')} labelWidth="sm" hint={t('booking.resource.field.shortDescriptionHint')}>
            <input
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              autoCapitalize="off"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('booking.resource.field.price')} labelWidth="sm">
            <span className="inline-flex items-center gap-2">
              <input
                type="text"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(digits(e.target.value, true))}
                placeholder="—"
                className="w-24 rounded-md border border-input bg-background px-2.5 py-1.5 text-right text-sm tabular-nums text-foreground outline-none focus:ring-2 focus:ring-ring"
              />
              <input
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                maxLength={3}
                aria-label={t('booking.resource.field.currency')}
                className="w-14 rounded-md border border-input bg-background px-2 py-1.5 text-center text-sm uppercase text-foreground outline-none focus:ring-2 focus:ring-ring"
              />
            </span>
          </FormRow>
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <FormRow label={t('booking.resource.field.locationName')} labelWidth="sm">
              <input
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
            <FormRow label={t('booking.resource.field.address')} labelWidth="sm">
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
          </div>
          <FormRow label={t('booking.resource.field.categories')} labelWidth="sm" hint={t('booking.resource.field.categoriesHint')}>
            <ChipInput value={categories} onChange={setCategories} placeholder={t('booking.resource.field.categoriesPlaceholder')} aria-label={t('booking.resource.field.categories')} />
          </FormRow>
          <FormRow label={t('booking.resource.field.coverImageUrl')} labelWidth="sm">
            <input
              value={coverImageUrl}
              onChange={(e) => setCoverImageUrl(e.target.value)}
              placeholder={t('booking.resource.field.imageUrlPlaceholder')}
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('booking.resource.field.images')} labelWidth="sm">
            <ImageUrlList value={imageUrls} onChange={setImageUrls} placeholder={t('booking.resource.field.imageUrlPlaceholder')} aria-label={t('booking.resource.field.images')} />
          </FormRow>
        </div>
      )}
    </FormDialog>
  );
}
